import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DailyIframe, { type DailyCall } from "@daily-co/daily-js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link as RouterLink,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import {
  endVideo,
  getSession,
  joinVideo,
  startVideo,
} from "@/api/connectApi";
import { DOCTOR_APPTS_QUERY_KEY } from "@/api/appointmentApi";
import { PageHeader } from "@/components/common/PageHeader";
import { apiErrorUserMessage, connectVideoConflictSeverity } from "@/lib/apiErrors";
import PrescriptionFormDialog from "@/components/prescription/PrescriptionFormDialog";
import type { DailyVideoCredentials } from "@/types/domain";

/**
 * In-browser video consultation (embedded player).
 * Doctor starts the room; patient polls until admitted, then both join with meeting tokens.
 */
export default function ConsultationRoomPage() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isPatient, isDoctor } = useAuth();

  const role = useMemo(
    () =>
      location.pathname.startsWith("/doctor/") ? ("doctor" as const) : ("patient" as const),
    [location.pathname],
  );

  const qc = useQueryClient();
  const [creds, setCreds] = useState<DailyVideoCredentials | null>(null);
  const [waitHint, setWaitHint] = useState<string | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [fatalSeverity, setFatalSeverity] = useState<"error" | "warning">("error");
  const [showPrescriptionDialog, setShowPrescriptionDialog] = useState(false);
  const frameRef = useRef<DailyCall | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const homePath = role === "doctor" ? "/doctor/appointments" : "/patient/appointments";

  const startMut = useMutation({
    mutationFn: () => {
      if (!appointmentId) throw new Error("Missing appointment");
      return startVideo(appointmentId);
    },
    onSuccess: (data) => setCreds(data),
    onError: (err: unknown) => {
      const msg = apiErrorUserMessage(err, "Could not start the call.");
      const is409 = isAxiosError(err) && err.response?.status === 409;
      setFatalSeverity(is409 ? connectVideoConflictSeverity(msg) : "error");
      setFatal(msg);
    },
  });

  const endMut = useMutation({
    mutationFn: () => {
      if (!appointmentId) throw new Error("Missing appointment");
      return endVideo(appointmentId);
    },
    onSuccess: async () => {
      if (frameRef.current) {
        try {
          await frameRef.current.destroy();
        } catch {
          /* ignore */
        }
        frameRef.current = null;
      }
      if (role === "doctor") {
        setShowPrescriptionDialog(true);
      } else {
        navigate(homePath);
      }
    },
  });

  useEffect(() => {
    if (!appointmentId || role !== "patient" || creds || fatal) {
      return;
    }
    let cancelled = false;

    const tick = async () => {
      try {
        const j = await joinVideo(appointmentId);
        if (cancelled) return;
        if (j.admitted && j.video) {
          setCreds(j.video);
          setWaitHint(null);
          return;
        }
        setWaitHint(j.message ?? "Waiting for the doctor to start…");
        const s = await getSession(appointmentId);
        if (cancelled) return;
        if (s.callStatus === "ENDED") {
          setFatalSeverity("error");
          setFatal("This consultation has ended.");
        }
      } catch (e: unknown) {
        if (cancelled) return;
        const msg = apiErrorUserMessage(e, "Unable to join.");
        const is409 = isAxiosError(e) && e.response?.status === 409;
        setFatalSeverity(is409 ? connectVideoConflictSeverity(msg) : "error");
        setFatal(msg);
      }
    };

    const id = window.setInterval(() => void tick(), 4000);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [appointmentId, role, creds, fatal]);

  useEffect(() => {
    if (!creds || !containerRef.current) {
      return;
    }
    let cancelled = false;
    const el = containerRef.current;
    const frame = DailyIframe.createFrame(el, {
      iframeStyle: {
        width: "100%",
        height: "min(72vh, 720px)",
        border: "0",
        borderRadius: "8px",
      },
      showLeaveButton: true,
    });
    frameRef.current = frame;
    void (async () => {
      try {
        await frame.join({ url: creds.roomUrl, token: creds.token });
      } catch (e) {
        if (!cancelled) {
          setFatalSeverity("error");
          const msg = e instanceof Error ? e.message : "Failed to join the consultation room.";
          setFatal(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
      void frame.destroy();
      if (frameRef.current === frame) {
        frameRef.current = null;
      }
    };
  }, [creds]);

  if (!appointmentId) {
    return <Navigate to={homePath} replace />;
  }

  if (role === "patient" && !isPatient) {
    return <Navigate to="/unauthorized" replace />;
  }
  if (role === "doctor" && !isDoctor) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <Box>
      <PageHeader
        title="Video consultation"
        subtitle="Private in-browser video consultation."
        actions={
          <Button component={RouterLink} to={homePath} variant="outlined" size="small">
            Back
          </Button>
        }
      />

      {fatal && (
        <Alert severity={fatalSeverity} sx={{ mb: 2 }}>
          {fatal}
        </Alert>
      )}

      {role === "doctor" && !creds && !fatal && (
        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="body2" color="text.secondary">
              When you are ready, start the consultation. The patient can join after you open the
              room.
            </Typography>
            <Button
              variant="contained"
              onClick={() => startMut.mutate()}
              disabled={startMut.isPending}
            >
              {startMut.isPending ? <CircularProgress size={22} color="inherit" /> : "Start call"}
            </Button>
          </Stack>
        </Paper>
      )}

      {role === "patient" && !creds && !fatal && (
        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {waitHint ?? "Connecting…"}
            </Typography>
            <CircularProgress size={28} />
          </Stack>
        </Paper>
      )}

      <Box ref={containerRef} sx={{ mb: 2 }} />

      {creds && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="error"
            disabled={endMut.isPending}
            onClick={() => endMut.mutate()}
          >
            {endMut.isPending ? "Ending…" : "End consultation"}
          </Button>
        </Stack>
      )}

      {appointmentId && (
        <PrescriptionFormDialog
          open={showPrescriptionDialog}
          appointmentId={appointmentId}
          onComplete={() => {
            qc.invalidateQueries({ queryKey: DOCTOR_APPTS_QUERY_KEY });
            navigate(homePath);
          }}
          onError={(msg) => {
            setFatal(msg);
            setShowPrescriptionDialog(false);
          }}
        />
      )}
    </Box>
  );
}
